/**
 * 마스킹 (CLAUDE.md 규칙 4: 개인정보는 앱을 떠나기 전에 지운다).
 *
 * 여기 있는 것은 전부 순수 함수다. 에이전트는 span 속성에, 서버는 저장 직전 2차 마스킹에
 * 같은 구현을 쓴다(4회차 결정 4). OTel에 묶인 SpanProcessor는 에이전트 쪽에 남는다.
 *
 * 2026-09-07 결정 7로 **대상이 좁아졌다.** 지우는 것은 URL 쿼리의 서명 토큰(`st=` 등)과
 * URL의 userinfo, 그리고 그 위에 얹는 패턴 치환뿐이다. SQL 문장의 리터럴·바인드 값,
 * 클라이언트 IP·User-Agent·예외 메시지는 사내 관제 도구라 그대로 둔다.
 * 헤더(`authorization`·`cookie`·`set-cookie`)와 본문은 계측 단계에서 아예 수집하지 않는다.
 *
 * 손대는 키를 화이트리스트로 좁힌 것은 오버헤드 이유도 있다. 13회차까지는 **모든** 문자열
 * 속성에 정규식 9개를 돌려 요청당 33us를 썼는데, 그중 대부분이 `user_agent.original`·
 * `network.*`처럼 개인정보가 들어올 일이 없는 키에서 헛돈 것이었다.
 */

export const EMAIL_TOKEN = '[email]';
export const PHONE_TOKEN = '[phone]';
export const RRN_TOKEN = '[rrn]';
export const REDACTED_TOKEN = '[redacted]';
export const SQL_PLACEHOLDER = '?';

/**
 * 치환 전에 먼저 떼어 두는 값들. 형태가 개인정보와 겹쳐 오탐을 낸다.
 * UUID, traceId(32 hex), spanId(16 hex), ISO 타임스탬프·날짜.
 */
const PROTECTED_PATTERNS: readonly RegExp[] = [
  /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b/g,
  /\b[0-9a-fA-F]{32}\b/g,
  /\b[0-9a-fA-F]{16}\b/g,
  /\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?/g,
];

/** 자리표시 구분자. 사설 사용 영역이라 실제 로그 문자열에 나올 일이 없다 */
const SENTINEL = String.fromCharCode(0xe000);
const PLACEHOLDER = new RegExp(`${SENTINEL}(\\d+)${SENTINEL}`, 'g');

/**
 * 이메일. `%40`으로 인코딩된 것도 잡는다. TLD 길이는 제한하지 않는다(`a@b.c`).
 * 로컬 파트는 영숫자 런의 시작에서만, 64자까지만 매칭한다. `@` 없는 긴 base64·JWT 런에서
 * 모든 위치를 다시 시도하며 O(n²)로 늘어지던 것을 막는다.
 */
const EMAIL_PATTERN =
  /(?<![A-Za-z0-9._%+-])[A-Za-z0-9._%+-]{1,64}(?:@|%40)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;

/** 주민등록번호. 뒷자리 첫 숫자(성별코드)는 1~8, 앞 6자리는 실제 날짜여야 한다 */
const RRN_PATTERN = /(?<!\d)(\d{6})-?([1-8]\d{6})(?!\d)/g;

/** 한국 전화번호. 국제표기, 휴대폰(구분자 없어도 됨), 지역번호(구분자 필수) */
const PHONE_PATTERNS: readonly RegExp[] = [
  /(?<!\d)\+82[-\s]?0?(?:1[016789]|2|[3-6]\d)[-\s]?\d{3,4}[-\s]?\d{4}(?!\d)/g,
  /(?<!\d)01[016789][-\s]?\d{3,4}[-\s]?\d{4}(?!\d)/g,
  /(?<!\d)0(?:2|[3-6]\d)[-\s]\d{3,4}[-\s]\d{4}(?!\d)/g,
];

function isRrnDate(front: string): boolean {
  const month = Number(front.slice(2, 4));
  const day = Number(front.slice(4, 6));
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

/** 오탐 대상을 자리표시로 빼 둔다. 치환이 끝나면 restore로 되돌린다 */
function protect(text: string): { masked: string; tokens: string[] } {
  const tokens: string[] = [];
  let masked = text.includes(SENTINEL) ? text.replaceAll(SENTINEL, '') : text;
  for (const pattern of PROTECTED_PATTERNS) {
    masked = masked.replace(pattern, (match) => {
      tokens.push(match);
      return `${SENTINEL}${tokens.length - 1}${SENTINEL}`;
    });
  }
  return { masked, tokens };
}

function restore(text: string, tokens: readonly string[]): string {
  if (tokens.length === 0) return text;
  return text.replace(PLACEHOLDER, (_all, index: string) => tokens[Number(index)] ?? '');
}

/**
 * 이메일·전화번호·주민번호(+ 설정으로 받은 추가 정규식)를 치환한다.
 * 오탐 방지를 위해 UUID·hex id·타임스탬프는 먼저 빼 두고 마지막에 되돌린다.
 */
export function scrubText(text: string, extraPatterns: readonly RegExp[] = []): string {
  if (text === '') return text;
  const { masked, tokens } = protect(text);
  let out = masked.replace(EMAIL_PATTERN, EMAIL_TOKEN);
  out = out.replace(RRN_PATTERN, (match: string, front: string) =>
    isRrnDate(front) ? RRN_TOKEN : match,
  );
  for (const pattern of PHONE_PATTERNS) out = out.replace(pattern, PHONE_TOKEN);
  for (const pattern of extraPatterns) {
    // compilePatterns가 이미 g 플래그를 붙여 두므로 다시 만들지 않는다
    const global = pattern.flags.includes('g')
      ? pattern
      : new RegExp(pattern.source, `${pattern.flags}g`);
    out = out.replace(global, REDACTED_TOKEN);
  }
  return restore(out, tokens);
}

const IDENTIFIER_CHAR = /[A-Za-z0-9_$."]/;
/**
 * sticky(`y`) 정규식이다. `lastIndex`를 옮겨 **원문 위에서 바로** 맞춰 본다.
 *
 * 13회차 전에는 `$`와 숫자를 만날 때마다 `sql.slice(i)`로 나머지 전체를 복사한 뒤 `^` 정규식을
 * 돌렸다. 긴 문장에서 O(n^2)이고, pg span이 끝날 때마다 매번 돈다(오버헤드 예산 직결).
 * sticky는 같은 것을 복사 없이 한다. `exec` 전에 `lastIndex`를 반드시 세울 것.
 */
const NUMBER_LITERAL = /\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/y;
const DOLLAR_TAG = /\$(?:[A-Za-z_][A-Za-z_0-9]*)?\$/y;
const DOLLAR_PARAM = /\$\d+/y;
const WORD_RUN = /\w+/y;

/** sticky 정규식을 `at`에서 맞춰 본다. 안 맞으면 undefined */
function stickyAt(pattern: RegExp, text: string, at: number): string | undefined {
  pattern.lastIndex = at;
  return pattern.exec(text)?.[0];
}

/**
 * SQL에서 인라인 리터럴만 `?`로 바꾼다.
 * **기본 경로에서는 부르지 않는다**(2026-09-07 결정 7: 리터럴을 남긴다).
 * `ScrubOptions.sql`을 켜야 돈다. 되돌릴 길로 남겨 둔 것이다.
 *
 * 유지: 식별자, 키워드, `$1` 자리표시, 주석. 주석은 이후 회차의 DB 워처가
 * SQL 주석으로 앱 트레이스를 맞추는 데 쓴다. 주석 안의 개인정보는 scrubText가 다시 훑는다.
 */
export function scrubSql(sql: string): string {
  let out = '';
  let i = 0;
  while (i < sql.length) {
    const ch = sql[i] as string;
    const next = sql[i + 1];

    if (ch === '-' && next === '-') {
      const end = sql.indexOf('\n', i);
      const stop = end === -1 ? sql.length : end;
      out += sql.slice(i, stop);
      i = stop;
      continue;
    }
    if (ch === '/' && next === '*') {
      let depth = 1;
      let j = i + 2;
      while (j < sql.length && depth > 0) {
        if (sql[j] === '/' && sql[j + 1] === '*') {
          depth += 1;
          j += 2;
        } else if (sql[j] === '*' && sql[j + 1] === '/') {
          depth -= 1;
          j += 2;
        } else j += 1;
      }
      out += sql.slice(i, j);
      i = j;
      continue;
    }
    if (ch === '"') {
      // 따옴표 식별자는 값이 아니다. 그대로 둔다
      let j = i + 1;
      while (j < sql.length) {
        if (sql[j] === '"' && sql[j + 1] === '"') j += 2;
        else if (sql[j] === '"') {
          j += 1;
          break;
        } else j += 1;
      }
      out += sql.slice(i, j);
      i = j;
      continue;
    }
    if (ch === '$') {
      const marker = stickyAt(DOLLAR_TAG, sql, i);
      if (marker !== undefined) {
        const close = sql.indexOf(marker, i + marker.length);
        i = close === -1 ? sql.length : close + marker.length;
        out += SQL_PLACEHOLDER;
        continue;
      }
      const param = stickyAt(DOLLAR_PARAM, sql, i);
      if (param !== undefined) {
        out += param;
        i += param.length;
        continue;
      }
      out += ch;
      i += 1;
      continue;
    }
    if (ch === "'" || ((ch === 'E' || ch === 'e') && next === "'")) {
      const backslashEscapes = ch !== "'";
      let j = backslashEscapes ? i + 2 : i + 1;
      while (j < sql.length) {
        if (backslashEscapes && sql[j] === '\\') j += 2;
        else if (sql[j] === "'" && sql[j + 1] === "'") j += 2;
        else if (sql[j] === "'") {
          j += 1;
          break;
        } else j += 1;
      }
      out += SQL_PLACEHOLDER;
      i = j;
      continue;
    }
    if (ch >= '0' && ch <= '9') {
      const prev = i > 0 ? (sql[i - 1] as string) : '';
      if (!IDENTIFIER_CHAR.test(prev)) {
        const number = stickyAt(NUMBER_LITERAL, sql, i);
        if (number !== undefined) {
          out += SQL_PLACEHOLDER;
          i += number.length;
          continue;
        }
      }
      const text = stickyAt(WORD_RUN, sql, i) ?? ch;
      out += text;
      i += text.length;
      continue;
    }
    out += ch;
    i += 1;
  }
  return out;
}

/**
 * 문장 끝에 붙은 sqlcommenter 주석(`… /*traceparent='00-…-01'*` + `/`) 하나를 떼어 낸다 (9회차 결정 1).
 *
 * 에이전트가 DB 세션과 요청을 잇기 위해 붙이는 것이라 **DB 쪽에서는 필요하지만**
 * 저장하는 SQL 문장에는 남을 이유가 없다. 요청마다 값이 달라 같은 문장이 서로 다른 문자열이 되고,
 * 상세 화면에서도 잡음이다. 워처는 값을 읽은 **뒤에** 이것으로 문장만 남긴다.
 *
 * 끝에 붙은 것 하나만 본다. 앱이 원래 쓰던 주석은 건드리지 않는다.
 */
export function stripSqlCommenter(sql: string): string {
  const trimmed = sql.trimEnd();
  if (!trimmed.endsWith('*/')) return sql;
  const open = trimmed.lastIndexOf('/*');
  if (open < 0) return sql;
  const body = trimmed.slice(open + 2, trimmed.length - 2);
  // sqlcommenter는 `키='값'`을 쉼표로 잇는다. 그 모양이 아니면 앱이 쓴 주석이라 남긴다
  if (!/^[a-z0-9_]+='[^']*'(?:,[a-z0-9_]+='[^']*')*$/i.test(body)) return sql;
  return trimmed.slice(0, open).trimEnd();
}

/** 쿼리 키를 비교용으로 정규화한다. 퍼센트 인코딩(`%73t`)과 `+`를 풀고 소문자로 */
function decodeQueryKey(key: string): string {
  try {
    return decodeURIComponent(key.replace(/\+/g, ' ')).toLowerCase();
  } catch {
    return key.toLowerCase();
  }
}

/** 푼 값 안에서 `?키=`·`&키=` 모양의 키를 찾는다 */
const ENCODED_PAIR_KEY = /[?&#;]([^=&?#\s"'<>]+)=/g;

/** `%` 뒤 두 글자를 바이트로. 16진이 아니면 -1 */
function escapedByte(text: string, at: number): number {
  if (at + 2 >= text.length || text.charCodeAt(at) !== 0x25) return -1;
  const high = hexDigit(text.charCodeAt(at + 1));
  const low = hexDigit(text.charCodeAt(at + 2));
  return high < 0 || low < 0 ? -1 : (high << 4) | low;
}

function hexDigit(code: number): number {
  if (code >= 0x30 && code <= 0x39) return code - 0x30;
  if (code >= 0x41 && code <= 0x46) return code - 0x37;
  if (code >= 0x61 && code <= 0x66) return code - 0x57;
  return -1;
}

/**
 * `decodeURIComponent`가 **던지지 않을** 입력인가 (60회차, B-01).
 *
 * 깨진 표기(`%` 뒤에 16진 두 자리가 없음, 올바른 UTF-8이 아닌 바이트열)에서 예외를 만들고 삼키는 데
 * 쌍 하나에 수 us가 들었다. 인증 없는 16KB URL 하나(`x=%&x=%&…`)가 마스킹에서 60ms를 썼다.
 * 여기서 먼저 판정해 예외 없이 건너뛴다. 판정은 ECMAScript `Decode`와 같다 — 바이트열이
 * Unicode 표 3-7의 올바른 UTF-8이어야 한다(겹침 표기·대리 영역·U+10FFFF 초과는 거부).
 * 결과가 같다는 것은 시험이 모든 1·2바이트 조합과 3·4바이트 경계값으로 고정한다.
 */
export function canDecodeURIComponent(text: string): boolean {
  let at = text.indexOf('%');
  while (at !== -1) {
    const lead = escapedByte(text, at);
    if (lead < 0) return false;
    at += 3;
    if (lead >= 0x80) {
      let follow: number;
      let min = 0x80;
      let max = 0xbf;
      if (lead >= 0xc2 && lead <= 0xdf) follow = 1;
      else if (lead >= 0xe0 && lead <= 0xef) {
        follow = 2;
        if (lead === 0xe0) min = 0xa0;
        else if (lead === 0xed) max = 0x9f;
      } else if (lead >= 0xf0 && lead <= 0xf4) {
        follow = 3;
        if (lead === 0xf0) min = 0x90;
        else if (lead === 0xf4) max = 0x8f;
      } else return false;
      for (let i = 0; i < follow; i += 1) {
        const byte = escapedByte(text, at);
        // 첫 이음 바이트만 범위가 좁다. 나머지는 80~BF
        if (byte < (i === 0 ? min : 0x80) || byte > (i === 0 ? max : 0xbf)) return false;
        at += 3;
      }
    }
    at = text.indexOf('%', at);
  }
  return true;
}

/**
 * 인코딩된 값 안에 토큰 쌍이 숨어 있는가 (`returnUrl=%2Fstream%3Fst%3Dabc`).
 *
 * 값을 **두 번까지** 풀어 본다(`%253F`는 두 번 인코딩한 `?`다). `%`가 없는 값은 보지 않으므로
 * 보통 URL에는 비용이 없다. 못 푸는 값(깨진 `%` 표기)은 숨은 것이 없다고 본다 — 60회차부터 예외를
 * 만들지 않고 `canDecodeURIComponent`로 미리 가른다(결과는 같다).
 */
function hidesToken(value: string, tokenKeys: ReadonlySet<string>): boolean {
  let text = value;
  for (let round = 0; round < 2 && text.includes('%'); round += 1) {
    const plain = text.replace(/\+/g, ' ');
    if (!canDecodeURIComponent(plain)) return false;
    const decoded = decodeURIComponent(plain);
    if (decoded === text) return false;
    text = decoded;
    for (const match of text.matchAll(ENCODED_PAIR_KEY)) {
      if (tokenKeys.has(decodeQueryKey(match[1] as string))) return true;
    }
  }
  return false;
}

/**
 * 쿼리 문자열(`a=1&st=xxx`)에서 토큰 키의 값만 지운다. 키는 대소문자를 무시한다.
 *
 * 쌍은 `&`에서 나뉘지만 **값 안의 `?`도 쌍의 경계로 본다.** 쿼리 값에는 `?`가 인코딩 없이 올 수
 * 있어서 `returnUrl=/stream?st=abc`처럼 URL이 통째로 실리면 `&`로만 나눌 때 토큰이 앞 쌍의 값에
 * 묻혀 남는다(`scrubMessage`가 40회차에 막은 것과 같은 모양). 토큰 쌍을 찾으면 다음 `&`까지를
 * 모두 값으로 보고 지운다 — 토큰 값에 `?`가 섞였을 때 뒤가 남지 않게 더 지우는 쪽을 고른다.
 * 값이 **인코딩된** URL이고 그 안에 토큰 쌍이 있으면(`returnUrl=%2Fstream%3Fst%3Dabc`) 그 값을
 * 통째로 지운다(`hidesToken`). 토큰이 없는 쿼리는 글자 하나 바뀌지 않는다.
 */
export function scrubQueryString(query: string, tokenKeys: ReadonlySet<string>): string {
  if (query === '') return query;
  // undici 계측은 url.query에 `?`를 붙여 준다. 떼고 처리한 뒤 다시 붙인다
  if (query.startsWith('?')) return `?${scrubQueryString(query.slice(1), tokenKeys)}`;
  // 홀수 자리가 구분자(`&`·`?`)다
  const parts = query.split(/([&?])/);
  let out = '';
  let redacting = false;
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i] as string;
    if (i % 2 === 1) {
      if (part === '&') redacting = false;
      if (!redacting) out += part;
      continue;
    }
    if (redacting) continue;
    const eq = part.indexOf('=');
    // 값이 없는 키(`?st`)는 지울 값도 없다
    const key = eq === -1 ? undefined : part.slice(0, eq);
    if (key !== undefined && tokenKeys.has(decodeQueryKey(key))) {
      out += `${key}=${REDACTED_TOKEN}`;
      redacting = true;
    } else if (
      key !== undefined &&
      part.includes('%', eq) &&
      hidesToken(part.slice(eq + 1), tokenKeys)
    ) {
      out += `${key}=${REDACTED_TOKEN}`;
    } else {
      out += part;
    }
  }
  return out;
}

/** URL 또는 요청 대상 경로에서 토큰 쿼리 값과 userinfo를 지운다. 프래그먼트는 유지 */
export function scrubUrl(value: string, tokenKeys: ReadonlySet<string>): string {
  const hash = value.indexOf('#');
  const fragment = hash === -1 ? '' : value.slice(hash);
  const withoutFragment = hash === -1 ? value : value.slice(0, hash);

  const mark = withoutFragment.indexOf('?');
  const head = mark === -1 ? withoutFragment : withoutFragment.slice(0, mark);
  const query = mark === -1 ? '' : withoutFragment.slice(mark + 1);

  // http://user:pass@host/... 의 자격증명. authority 안의 **마지막** `@`까지 지운다 (41회차) —
  // 비밀번호에 `@`가 든 `https://alice:p@ss@host`에서 첫 `@`에 멈추면 `ss@host`가 남는다
  const safeHead = head.replace(/(:\/\/)[^/]*@/, `$1${REDACTED_TOKEN}@`);
  const queryPart = mark === -1 ? '' : `?${scrubQueryString(query, tokenKeys)}`;
  return `${safeHead}${queryPart}${fragment}`;
}

/**
 * 문장 안의 `?키=값`·`&키=값` 한 쌍.
 *
 * 값은 `&`·`?`·`#`·공백·`"`·꺾쇠에서 끝난다. `'`는 URL 쿼리에 그대로 올 수 있어 값의 일부로 본다.
 * **`?`에서 끊는 것은 47회차부터다.** 쿼리 값에 URL이 인코딩 없이 통째로 실리면
 * (`returnUrl=/stream?st=abc`) 뒤 쌍이 앞 쌍의 값에 묻혀 토큰이 남았다(`scrubQueryString`은 45회차에
 * 막았다). 토큰 쌍의 값은 `?`를 넘어 끝까지 지운다(`TOKEN_VALUE_TAIL`).
 * 값이 따옴표로 시작하면 닫는 따옴표까지(없으면 끝까지), 인증 방식 낱말(`Bearer abc`)이면 뒤 낱말까지
 * 값으로 본다. 모자라게 지우면 토큰이 남는다 — 경계가 애매하면 더 지우는 쪽을 고른다 (40회차 리뷰 R-01).
 */
const MESSAGE_QUERY_PAIR =
  /([?&])([^=&?#\s"'<>]+)=("[^"]*"?|'[^']*'?|(?:(?:bearer|basic|digest|negotiate|token)\s+)?[^&?#\s"<>]*)/gi;
/** 토큰 값의 나머지. 따옴표 없는 값은 `?`를 넘어 `&`·`#`·공백·`"`·꺾쇠까지 지운다 */
const TOKEN_VALUE_TAIL = /[^&#\s"<>]*/y;
/**
 * 문장 안의 `://userinfo@`. URL의 userinfo에는 공백이 그대로 올 수 없으므로(인코딩된다) 공백에서 멈춘다.
 * 따옴표는 올 수 있어 넘는다 — 비밀번호의 `'`에서 멈추면 자격증명이 통째로 남는다 (40회차 리뷰 R-02).
 * authority 안의 **마지막** `@`까지 지운다(41회차). authority는 `/`·`?`·`#`에서 끝난다
 */
const MESSAGE_USERINFO = /(:\/\/)[^/?#\s]+@/g;

/**
 * 사람이 읽는 문장(예외 메시지·span 상태 메시지) 안의 URL 서명 토큰과 userinfo를 지운다 (40회차).
 *
 * `scrubUrl`은 값 전체를 URL 하나로 보고 첫 `?`부터 끝까지를 쿼리로 읽는다. 문장에 쓰면 둘이
 * 틀린다. 둘째 URL의 토큰은 앞 쌍의 값 속에 묻혀 **안 지워지고**(규칙 4 위반), 첫 토큰 뒤의 문장은
 * 값으로 읽혀 통째로 지워진다(25회차 결정 항목). 여기서는 쌍을 **모두** 찾아 토큰 키의 값만 지운다.
 * 서명 토큰에는 공백·따옴표가 없으므로 값이 거기서 끝난다고 보아도 토큰은 다 지워진다.
 */
export function scrubMessage(text: string, tokenKeys: ReadonlySet<string>): string {
  if (text === '') return text;
  const safe = text.includes('://') ? text.replace(MESSAGE_USERINFO, `$1${REDACTED_TOKEN}@`) : text;
  if (!safe.includes('=')) return safe;
  let out = '';
  let from = 0;
  const pairs = MESSAGE_QUERY_PAIR;
  pairs.lastIndex = 0;
  for (let match = pairs.exec(safe); match !== null; match = pairs.exec(safe)) {
    const at = match.index;
    const [pair, lead, key, value] = match as unknown as [string, string, string, string];
    const quoted = value.startsWith('"') || value.startsWith("'");
    const token =
      tokenKeys.has(decodeQueryKey(key)) || (value.includes('%') && hidesToken(value, tokenKeys));
    if (!token) {
      // 따옴표로 감싼 값은 안쪽도 본다(`?returnUrl="/stream?st=abc"`). 여는 따옴표 바로 뒤부터 다시
      // 찾는다. 값 하나로 넘기면 그 안의 토큰이 남는다 (47회차)
      if (quoted) pairs.lastIndex = at + lead.length + key.length + 2;
      continue;
    }
    let end = at + pair.length;
    if (!quoted) {
      TOKEN_VALUE_TAIL.lastIndex = end;
      end += TOKEN_VALUE_TAIL.exec(safe)?.[0].length ?? 0;
    }
    out += `${safe.slice(from, at)}${lead}${key}=${REDACTED_TOKEN}`;
    from = end;
    // 지운 자리 안은 다시 찾지 않는다
    pairs.lastIndex = end;
  }
  return from === 0 ? safe : out + safe.slice(from);
}

const SQL_ATTRIBUTE_KEYS = new Set(['db.statement', 'db.query.text']);
const QUERY_ATTRIBUTE_KEYS = new Set(['url.query']);
const URL_ATTRIBUTE_KEYS = new Set([
  'url.full',
  'url.original',
  'url.path',
  'http.url',
  'http.target',
  'http.route',
  // 요청을 보낸 화면(Referer). URL이라 쿼리의 서명 토큰(`st=`)이 딸려 온다 (22-C회차)
  'http.request.header.referer',
]);
/**
 * 서명 토큰이 실릴 수 있는 속성 키 전부(URL·쿼리). 마스킹이 도중에 실패하면 에이전트가 이 키들을
 * 통째로 지우고 넘긴다(60회차, B-05 fail-closed). 목록의 원본은 위 두 집합이다
 */
export const TOKEN_BEARING_ATTRIBUTE_KEYS: readonly string[] = [
  ...URL_ATTRIBUTE_KEYS,
  ...QUERY_ATTRIBUTE_KEYS,
];
/**
 * span 상태 메시지. 사람이 쓴 문자열이 섞일 수 있어 패턴 치환을 얹는다.
 * 40회차부터 **URL 토큰 마스킹을 먼저 건다.** 계측이 예외 메시지를 그대로 옮겨 싣는 자리다 —
 * undici는 요청 오류 때 `recordException`과 `setStatus({ message: error.message })`를 함께 해서,
 * `exception.message`에서 지운 토큰이 여기로는 그대로 나갔다.
 */
const STATUS_MESSAGE_KEYS = new Set(['status.message']);
/**
 * 예외 메시지 (16회차). 메시지 자체는 저장한다(2026-09-07 결정 3). 다만 외부 호출이 실패하면
 * 메시지에 URL이 통째로 실리고 거기에 서명 토큰(`st=`)이 딸려 온다. 규칙 4는 그 토큰만은
 * 예외 없이 지우라고 하므로 **토큰 값과 userinfo만** 지우고 나머지는 손대지 않는다(`scrubMessage`).
 * 이메일·전화번호 패턴 치환은 걸지 않는다. 결정 3이 메시지를 그대로 두기로 한 것이라
 * 여기서 다시 지우면 오류를 읽을 수 없다.
 */
const MESSAGE_KEYS = new Set([
  'exception.message',
  // 속성은 아니지만 에이전트가 span 이름과 실시간 `currentSpan`에 같은 규칙을 건다.
  // pg 계측은 prepared statement 이름을 span 이름에 그대로 싣는다 (40회차 리뷰)
  'span.name',
]);

export interface ScrubOptions {
  /**
   * 끄면 **패턴 치환(이메일·전화·주민번호·추가 정규식)과 SQL 손질만** 멈춘다. 기본 true.
   * URL 쿼리의 서명 토큰과 userinfo 는 끌 수 없다(60회차, B-04). 규칙 4가 예외 없이 앱 안에서
   * 지우라고 한 것이라, 설정 한 줄로 원문이 앱을 떠나지 않게 한다. `queryKeys`의 기본 키를 못 빼게 한
   * 49회차와 같은 방식이다.
   */
  enabled: boolean;
  /** 이메일·전화·주민번호 패턴 치환 */
  patterns: boolean;
  /**
   * SQL 인라인 리터럴을 `?`로 지운다. **기본은 끈다**(2026-09-07 결정 7).
   * 되돌릴 길만 남겨 둔 스위치다. sqlcommenter 주석 제거는 이 값과 무관하게 항상 한다.
   */
  sql: boolean;
  /** 값을 지울 쿼리 키(대소문자 무시) */
  queryKeys: readonly string[];
  /** 설정에서 받은 추가 정규식 */
  extraPatterns: readonly RegExp[];
  /**
   * URL·쿼리·메시지 키에서 마스킹할 입력 길이 상한(자). 넘는 부분은 자르고 `[truncated]`를 붙인다.
   * 비우면 `DEFAULT_SCRUB_MAX_INPUT_CHARS`(16KB). SQL 키에는 걸지 않는다 (60회차, B-01)
   */
  maxInputChars?: number;
}

/** 마스킹 입력 길이 상한 기본값. Node 기본 요청 헤더 상한(16KB)과 같다 */
export const DEFAULT_SCRUB_MAX_INPUT_CHARS = 16_384;

/** 상한에 잘린 값 끝에 붙는 표시 */
export const TRUNCATED_TOKEN = '[truncated]';

/** 끝에 걸린 반쪽 퍼센트 표기. `%`·`%X`·`%25`·`%25X` */
const TRAILING_PARTIAL_ESCAPE = /%(?:25)?[0-9a-f]?$/i;
/** 끝에 걸린 비ASCII 바이트 표기(한 번·두 번 인코딩). 여러 바이트 글자의 반쪽일 수 있다 */
const TRAILING_HIGH_BYTE = /%(?:25)?[89a-f][0-9a-f]$/i;
/** authority 가 끝나는 글자. 이것이 없으면 authority 가 잘린 끝까지 이어진다 */
const AUTHORITY_END = /[/?#]/;

/**
 * 긴 값을 상한까지 자른다 (60회차, B-01).
 *
 * 끝에 반쪽 표기(`%3`, 여러 바이트 글자의 앞 바이트)가 남으면 그 값을 풀지 못해 **안에 숨은 토큰을
 * 못 찾고** 토큰 앞부분이 남는다. 그래서 끝의 반쪽 표기와 비ASCII 바이트 표기를 모두 떼고, 짝 없는
 * 상위 대리 글자도 뗀다. 어차피 버리는 꼬리라 조금 더 떼는 쪽을 고른다.
 *
 * **userinfo 가 잘리는 자리에 걸리면 끝까지 지운다.** `https://alice:SECRET…@host`를 `@` 앞에서 자르면
 * userinfo 규칙(`://…@`)이 더는 맞지 않아 자격 증명 앞부분이 그대로 남았다. 비밀번호에 `@`가 든 경우
 * (`alice:p@ss@host`)는 첫 `@` 뒤에서 잘려도 뒷부분이 남는다. 그래서 마지막 `://` 뒤 authority 가
 * 잘린 끝까지 이어지면(`/`·`?`·`#`가 없으면) 그 꼬리를 `[redacted]`로 바꾼다. 앞선 `://`의 authority 는
 * 뒤 `://`의 `/`에서 끝나므로 마지막 하나만 보면 된다. 공백에서는 멈추지 않는다 — URL 키의 userinfo
 * 규칙이 공백을 넘기 때문이다. 긴 값에서만 도는 일이라 조금 더 지우는 쪽을 고른다.
 */
export function capScrubInput(value: string, max: number = DEFAULT_SCRUB_MAX_INPUT_CHARS): string {
  if (value.length <= max) return value;
  let cut = value.slice(0, Math.max(0, max));
  for (;;) {
    const before = cut.length;
    cut = cut.replace(TRAILING_PARTIAL_ESCAPE, '').replace(TRAILING_HIGH_BYTE, '');
    const last = cut.charCodeAt(cut.length - 1);
    if (last >= 0xd800 && last <= 0xdbff) cut = cut.slice(0, -1);
    if (cut.length === before) break;
  }
  const scheme = cut.lastIndexOf('://');
  if (scheme !== -1 && !AUTHORITY_END.test(cut.slice(scheme + 3))) {
    cut = `${cut.slice(0, scheme + 3)}${REDACTED_TOKEN}`;
  }
  return `${cut}${TRUNCATED_TOKEN}`;
}

export const DEFAULT_QUERY_KEYS: readonly string[] = [
  'st',
  'token',
  'access_token',
  'refresh_token',
  'api_key',
  'apikey',
  'authorization',
];

/** 정규식 문자열을 컴파일한다. 잘못된 것은 조용히 버린다(규칙 2) */
export function compilePatterns(sources: readonly string[]): RegExp[] {
  const out: RegExp[] = [];
  for (const source of sources) {
    try {
      out.push(new RegExp(source, 'g'));
    } catch {
      // 설정 실수로 앱이 멈추지 않게 한다
    }
  }
  return out;
}
/**
 * 속성 키를 보고 SQL·URL·쿼리 처리를 고른 뒤 공통 패턴 치환을 얹는다.
 * 에이전트의 `Scrubber`(span 속성)와 서버의 2차 마스킹이 함께 쓴다.
 */
export class ValueScrubber {
  private readonly queryKeys: ReadonlySet<string>;

  constructor(protected readonly options: ScrubOptions) {
    this.queryKeys = new Set(options.queryKeys.map((key) => key.toLowerCase()));
  }

  /** 패턴 치환이 켜져 있는가. 토큰·userinfo 마스킹은 이 값과 무관하게 늘 돈다 (60회차) */
  get enabled(): boolean {
    return this.options.enabled;
  }

  /**
   * 문자열 하나. **화이트리스트에 있는 키만** 손댄다.
   * 목록에 없는 키(`user_agent.original`·`client.address`·`network.*`·`db.postgresql.values`
   * 등)는 그대로 통과한다 (2026-09-07 결정 3·7). `exception.message`만 예외로, URL이 섞였을 때
   * 서명 토큰과 userinfo를 지운다 (16회차). 문장 자체는 건드리지 않는다.
   */
  value(key: string, value: string): string {
    if (SQL_ATTRIBUTE_KEYS.has(key)) {
      // 꺼 두면 59회차까지처럼 문장을 손대지 않는다. 토큰 마스킹 대상이 아니다
      if (!this.options.enabled) return value;
      // traceparent 주석은 DB 쪽에서 읽는 것이라 저장하는 문장에는 남기지 않는다 (9회차 결정 1).
      // 리터럴은 남긴다. 상세 화면에서 어떤 값으로 돈 쿼리인지 봐야 한다
      const stripped = stripSqlCommenter(value);
      return this.options.sql ? scrubSql(stripped) : stripped;
    }
    // 패턴 치환을 얹지 않는다. 아래 화이트리스트와 달리 문장을 그대로 남겨야 한다
    if (MESSAGE_KEYS.has(key)) return scrubMessage(this.capped(value), this.queryKeys);
    let out: string;
    if (QUERY_ATTRIBUTE_KEYS.has(key)) out = scrubQueryString(this.capped(value), this.queryKeys);
    else if (URL_ATTRIBUTE_KEYS.has(key)) out = scrubUrl(this.capped(value), this.queryKeys);
    else if (STATUS_MESSAGE_KEYS.has(key)) out = scrubMessage(this.capped(value), this.queryKeys);
    else return value;
    return this.options.enabled && this.options.patterns
      ? scrubText(out, this.options.extraPatterns)
      : out;
  }

  /** 마스킹 전에 길이 상한을 건다. 긴 값 하나가 요청 경로를 붙잡지 않게 (60회차, B-01) */
  private capped(value: string): string {
    return capScrubInput(value, this.options.maxInputChars ?? DEFAULT_SCRUB_MAX_INPUT_CHARS);
  }
}
